/**
 * [rev16 Phase A] useModalStack — modal navigation stack hook
 *
 * Replaces the tri-state `showForm` / `editingTransaction` / `detailTransaction`
 * triplet (Decision D3) with a single ordered stack. The current top of the
 * stack is the only modal rendered. `push` always appends, so a Detail entry
 * is preserved beneath an Edit entry for the same `transactionId`; `pop`
 * then drops the top and Detail's `meta.scrollY` is restored on return via
 * `patchMeta` (Decision D8).
 *
 * Semantics:
 *   - `push(entry)`: append unconditionally (never collapses Detail→Edit).
 *   - `pop()`: drop the top entry; empty stack means no modal.
 *   - `replace(entry)`: swap the top entry (or seed the stack when empty).
 *   - `clear()`: drop every entry.
 *   - `patchMeta(index, meta)`: merge `meta` into a `detail` entry's existing
 *     meta object (used to persist scroll position across round-trips).
 *
 * The hook is intentionally non-generic — its stack type is `ModalEntry[]`
 * fixed at the source. Consumers do not supply a type parameter; the plan's
 * `useModalStack<ModalEntry[]>([])` call shape is invalid TypeScript for a
 * non-generic function and is NOT what the hook exposes.
 *
 * @see docs/superpowers/plans/2026-08-16-rev16-modal-redesign-plan.md (Section 2.3)
 */
import { useCallback, useState } from 'react';

import type { TransactionFormData } from '@/components/transactions/TransactionForm';

/** A single entry on the modal stack. Discriminated by `kind`. */
export type ModalEntry =
  | { kind: 'detail'; transactionId: string; meta?: { scrollY?: number } }
  | { kind: 'edit'; transactionId: string; focusField?: string }
  | { kind: 'add'; initialValues?: Partial<TransactionFormData> };

/** Public API returned by `useModalStack`. */
export interface ModalStackApi {
  /** Current stack contents (read-only snapshot). */
  stack: ModalEntry[];
  /** The topmost entry, or `null` when the stack is empty. */
  top: ModalEntry | null;
  /** Append a new modal; never collapses Detail→Edit. */
  push: (entry: ModalEntry) => void;
  /** Drop the top entry; no-op when the stack is empty. */
  pop: () => void;
  /** Swap the top entry; seeds the stack when empty. */
  replace: (entry: ModalEntry) => void;
  /** Drop every entry. */
  clear: () => void;
  /** Merge `meta` into an existing `detail` entry at `index`. */
  patchMeta: (index: number, meta: Record<string, unknown>) => void;
}

/**
 * Modal-stack state hook.
 *
 * @param initial - Optional seed entries (defaults to empty).
 */
export function useModalStack(initial: ModalEntry[] = []): ModalStackApi {
  const [stack, setStack] = useState<ModalEntry[]>(initial);

  const push = useCallback((entry: ModalEntry) => {
    setStack((prev) => [...prev, entry]);
  }, []);

  const pop = useCallback(() => {
    setStack((prev) => (prev.length > 0 ? prev.slice(0, -1) : prev));
  }, []);

  const replace = useCallback((entry: ModalEntry) => {
    setStack((prev) =>
      prev.length === 0 ? [entry] : [...prev.slice(0, -1), entry],
    );
  }, []);

  const clear = useCallback(() => {
    setStack([]);
  }, []);

  const patchMeta = useCallback(
    (index: number, meta: Record<string, unknown>) => {
      setStack((prev) =>
        prev.map((entry, i) =>
          i === index && entry.kind === 'detail'
            ? { ...entry, meta: { ...entry.meta, ...meta } }
            : entry,
        ),
      );
    },
    [],
  );

  const top = stack.length > 0 ? stack[stack.length - 1] ?? null : null;

  return { stack, top, push, pop, replace, clear, patchMeta };
}